import {Constants} from './view-constants';
import History from '@core/scripts/components/history';
import Landmark from '@core/scripts/components/landmark-adapter';

// used to delay the execution of a function to the next event loop.
// Allows the browser to update the UI before the function is executed.
function yieldToMain(cb) {
    if (!cb) return;
    return new Promise((resolve) => {
        setTimeout(() => {
            const result = cb();
            resolve(result);
        }, 0);
    });
}

function createUrlFromCurrentUrl(searchContext, url) {
    const currentUrl = url ? new URL(url) : new URL(window.location.href);
    const params = currentUrl.searchParams;
    if (searchContext) {
        params.delete('searchmode');
        params.delete('search_origin');
        params.set('disableRedirects', 'true');
    }
    return currentUrl;
}

function pushUrlToHistory(token, pathname, newParams, oldParams) {
    const newUrl = pathname ? pathname : new URL(window.location.href).pathname;
    const usedParams = newParams ? newParams : oldParams;
    const paramsToAdd = usedParams.toString() ? `?${usedParams}` : '';
    const urlWithParamsAdded = decodeURIComponent(`${newUrl}${paramsToAdd}`);
    History.pushURL(urlWithParamsAdded, token);
}

function editFilterParams(key, value, params) {
    const keyWithFilterPrefix = `${Constants.FILTER_PARAM_PREFIX}${key}`;
    const hasKey = params.has(keyWithFilterPrefix);
    if (!value) {
        params.delete(keyWithFilterPrefix);
    } else if (hasKey) {
        params.set(keyWithFilterPrefix, value);
    } else {
        params.append(keyWithFilterPrefix, value);
    }
    return params;
}
function replaceParamString(params, removeParam) {
    return params
        .split(',')
        .filter((param) => param !== removeParam)
        .join(',');
}

function applyFilterLandmark(clickedFilters, params, shopId, isSticky, previousParams, rootShopId) {
    const categoryFilter = clickedFilters.get(Constants.CATEGORY_FILTER_IDENTIFIER);
    if (!!categoryFilter) {
        applyFilterLandmarkForCategory(categoryFilter, shopId, isSticky, rootShopId);
        return;
    }

    if (clickedFilters.size > 0) {
        Array.from(clickedFilters.keys()).forEach((key) => {
            const keyWithFilterPrefix = `${Constants.FILTER_PARAM_PREFIX}${key}`;
            const filterTrackingId = clickedFilters.get(key).trackingId;
            const selectedFilterValues = params.get(keyWithFilterPrefix)?.split(',') || [];
            const previousFilterValues = previousParams.get(keyWithFilterPrefix)?.split(',') || [];
            const deselectedFilterValues = getDeselectedFilterValues(previousFilterValues, selectedFilterValues);

            selectedFilterValues.forEach((value) => {
                sendFilterLandmark(Constants.LANDMARK_FILTER_SELECTION, filterTrackingId, value, shopId, isSticky);
            });
            deselectedFilterValues.forEach((value) => {
                sendFilterLandmark(Constants.LANDMARK_FILTER_DESELECTION, filterTrackingId, value, shopId, isSticky);
            });
        });
    }
}

function getDeselectedFilterValues(previousFilterValues, selectedFilterValues) {
    return previousFilterValues.filter((v) => !selectedFilterValues.includes(v));
}

function applyFilterLandmarkForCategory(clickedCategory, shopId, isSticky, rootShopId) {
    const clickedShop = clickedCategory.clickedFilterOptions?.[0];
    const trackingId = clickedCategory.trackingId;

    if (clickedShop) {
        sendFilterLandmark(Constants.LANDMARK_FILTER_SELECTION, trackingId, clickedShop, shopId, isSticky);
    }
    const currentShopIsRootShop = shopId === rootShopId;
    if (!currentShopIsRootShop) {
        sendFilterLandmark(Constants.LANDMARK_FILTER_DESELECTION, trackingId, shopId, shopId, isSticky);
    }
}

function sendFilterLandmark(type, trackingId, filterValue, shopId, isSticky) {
    const shopIdFormatted = shopId ? `${shopId}` : '0';

    let landmarkData = {
        filter_click: `${type}|${trackingId}|${filterValue}`,
        filter_click_shopId: `${shopIdFormatted}`,
        fixation: isSticky ? Constants.LANDMARK_STICKY : Constants.LANDMARK_NON_STICKY,
    };

    Landmark.sendLandmark({
        landmark: 'userinterface',
        ...landmarkData,
    });
}

function deleteFilterParams(currentParams) {
    const params = new URLSearchParams(currentParams.toString());
    const paramList = [];

    for (const pair of params.entries()) {
        const identifier = pair[0];

        if (identifier.startsWith(Constants.FILTER_PARAM_PREFIX)) {
            paramList.push(identifier);
        }
    }

    paramList.forEach((paramToDelete) => {
        params.delete(paramToDelete);
    });
    return params;
}

function mergeSelectedFiltersWithParams(clickedFilters, currentParams) {
    const params = new URLSearchParams(currentParams.toString());

    if (clickedFilters.size > 0) {
        Array.from(clickedFilters.keys()).forEach((key) => {
            if (key === Constants.CATEGORY_FILTER_IDENTIFIER) {
                return;
            }
            const filterData = clickedFilters.get(key);
            const filterParam = filterData.clickedFilterOptions.join(',');
            editFilterParams(key, filterParam, params);
        });
    }
    return params;
}

function removeSingleFilterFromParams(key, removeValue, isRange, currentParams) {
    const filterIdentifier = Constants.FILTER_PARAM_PREFIX + key;
    const params = new URLSearchParams(currentParams.toString());
    const filteredParams = replaceParamString(params.get(filterIdentifier), removeValue);

    if (filteredParams === '' || isRange) {
        params.delete(filterIdentifier);
    } else {
        params.set(filterIdentifier, filteredParams);
    }
    return params;
}

function getContentAjaxUrl(path, params, contentAjaxUrl) {
    return `${contentAjaxUrl}/${path ? `${path}/` : ''}${params}`;
}

// While categories move to Neebo one by one, a category switch rendered in place here can land on a
// URL whose real page is served by Neebo. When the shopper is heading back to the page they came
// from in a full page load, returning to that history entry shows the page the URL actually serves.
// Returns the key of the nearest earlier history entry from another document when its path is
// `targetPathname`, otherwise null — also in browsers without the Navigation API.
function findEarlierDocumentEntryKey(targetPathname) {
    const navigation = window.navigation;
    const currentIndex = navigation?.currentEntry?.index ?? -1;
    if (currentIndex < 1) {
        return null;
    }

    const entries = navigation.entries();
    for (let index = currentIndex - 1; index >= 0; index--) {
        const entry = entries[index];
        if (!entry.sameDocument) {
            return entry.url && new URL(entry.url).pathname === targetPathname ? entry.key : null;
        }
    }
    return null;
}

export {
    yieldToMain,
    createUrlFromCurrentUrl,
    pushUrlToHistory,
    editFilterParams,
    replaceParamString,
    applyFilterLandmark,
    deleteFilterParams,
    mergeSelectedFiltersWithParams,
    removeSingleFilterFromParams,
    getContentAjaxUrl,
    findEarlierDocumentEntryKey,
};
